/* global window */

import axios from 'axios'
import url from 'url'

import MiscUtils, { PROD_STAGE } from './MiscUtils'

// TODO: Change endpoints when deploying app with URL rewrites
const CLOUD_FUNCTIONS_GCAL_API_URL = MiscUtils.STAGE === PROD_STAGE
  ? 'https://us-central1-fibo-collab.cloudfunctions.net/gCalApplicationApiV1/api'
  : 'https://us-central1-fibo-collab-dev.cloudfunctions.net/gCalApplicationApiV1/api'

const CLOUD_FUNCTIONS_INVOICES_API_URL = MiscUtils.STAGE === PROD_STAGE
  ? 'https://us-central1-fibo-collab.cloudfunctions.net/invoicesApplicationApiV1/api'
  : 'https://us-central1-fibo-collab-dev.cloudfunctions.net/invoicesApplicationApiV1/api'

const CLOUD_FUNCTIONS_CUSTOMERS_API_URL = MiscUtils.STAGE === PROD_STAGE
  ? 'https://us-central1-fibo-collab.cloudfunctions.net/customersApplicationApiV1/api/customers'
  : 'https://us-central1-fibo-collab-dev.cloudfunctions.net/customersApplicationApiV1/api/customers'

const CLOUD_FUNCTIONS_ESTIMATES_API_URL = MiscUtils.STAGE === PROD_STAGE
  ? 'https://us-central1-fibo-collab.cloudfunctions.net/estimatesApplicationApiV1/api'
  : 'https://us-central1-fibo-collab-dev.cloudfunctions.net/estimatesApplicationApiV1/api'

const API_VERSION = 'v1'

const CloudFunctionsApisUtils = {

  getUserCalendars: (userId, teamId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_GCAL_API_URL}/gcal/${API_VERSION}/calendars` +
      `?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}`
    return axios.get(serviceUrl)
      .then(calendarsResponse => {
        return calendarsResponse.data
      })
  },

  exchangeUserAccess: (userId, teamId, oauthOneTimeCode) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_GCAL_API_URL}/gcal/${API_VERSION}/users/access` +
      `?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}`
    // Send appRedirectUri to decouple backend from deployed instance
    const current = url.parse(window.location.href)
    const appRedirectUri = `${current.protocol}//${current.host}`
    const body = { oauthOneTimeCode, appRedirectUri }
    return axios.post(serviceUrl, body)
      .then(exchangeReponse => {
        return exchangeReponse.data
      })
  },

  deleteUserAccess: (userId, teamId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_GCAL_API_URL}/gcal/${API_VERSION}/users/access` +
      `?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}`
    return axios.delete(serviceUrl)
      .then(deleteResponse => {
        return deleteResponse.data
      })
  },

  syncImportedCalendar: (userId, teamId, calendarId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_GCAL_API_URL}/gcal/${API_VERSION}/calendars/import` +
      `?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}`
    const body = { calendars: [ calendarId ] }
    return axios.put(serviceUrl, body)
      .then(syncResponse => {
        return syncResponse.data
      })
  },

  removeImportedCalendar: (userId, teamId, calendarId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_GCAL_API_URL}/gcal/${API_VERSION}/calendars` +
      `?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}&calendarId=${encodeURIComponent(calendarId)}`
    return axios.delete(serviceUrl)
      .then(deleteResponse => {
        return deleteResponse.data
      })
  },

  importCalendars: (userId, teamId, calendars) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_GCAL_API_URL}/gcal/${API_VERSION}/calendars/import` +
      `?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}`
    const body = { calendars: calendars.map(calendar => calendar.id) }
    return axios.post(serviceUrl, body)
      .then(importResponse => importResponse.data)
  },

  downloadInvoicePdf: (teamId, invoiceId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_INVOICES_API_URL}/invoices/${API_VERSION}/download` +
      `?teamId=${teamId}&invoiceId=${invoiceId}&format=pdf`
    return axios.request({
      method: 'get',
      url: serviceUrl,
      responseType: 'arraybuffer'
    }).then(response => {
      return { headers: response.headers, content: response.data }
    })
  },

  sendInvoiceEmail: (teamId, invoiceId, emailInfo) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_INVOICES_API_URL}/invoices/${API_VERSION}/send` +
      `?teamId=${teamId}&invoiceId=${invoiceId}`
    return axios.post(serviceUrl, emailInfo)
      .then(response => response.data)
  },

  duplicateInvoice: (teamId, invoice, copyOptions) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_INVOICES_API_URL}/invoices/${API_VERSION}/duplicate` +
      `?teamId=${teamId}&invoiceId=${invoice.id}`
    return axios.post(serviceUrl, copyOptions)
      .then(response => response.data)
  },

  recordInvoiceManualPayment: (teamId, invoice, paymentDetails) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_INVOICES_API_URL}/invoices/${API_VERSION}/record-manual-payment` +
      `?teamId=${teamId}&invoiceId=${invoice.id}`
    return axios.post(serviceUrl, paymentDetails)
      .then(response => response.data)
  },

  downloadEstimatePdf: (teamId, estimateId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_ESTIMATES_API_URL}/estimates/${API_VERSION}/download` +
      `?teamId=${teamId}&estimateId=${estimateId}&format=pdf`
    return axios.request({
      method: 'get',
      url: serviceUrl,
      responseType: 'arraybuffer'
    }).then(response => {
      return { headers: response.headers, content: response.data }
    })
  },

  sendEstimateEmail: (teamId, estimateId, emailInfo) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_ESTIMATES_API_URL}/estimates/${API_VERSION}/send` +
      `?teamId=${teamId}&estimateId=${estimateId}`
    return axios.post(serviceUrl, emailInfo)
      .then(response => response.data)
  },

  duplicateEstimate: (teamId, estimate, copyOptions) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_ESTIMATES_API_URL}/estimates/${API_VERSION}/duplicate` +
      `?teamId=${teamId}&estimateId=${estimate.id}`
    return axios.post(serviceUrl, copyOptions)
      .then(response => response.data)
  },
  
  importCustomers: (teamId, customers) => {
    const url = `${CLOUD_FUNCTIONS_CUSTOMERS_API_URL}/${API_VERSION}/create/batch`
    const body = { teamId, customers: JSON.stringify(customers) }
    return axios
      .post(url, body)
      .then(importResponse => importResponse.data)
  },
  
  convertEstimateToInvoice: (teamId, estimateId) => {
    const serviceUrl = `${CLOUD_FUNCTIONS_ESTIMATES_API_URL}/estimates/${API_VERSION}/convert-to-invoice` +
      `?teamId=${teamId}&estimateId=${estimateId}`
    return axios.post(serviceUrl)
      .then(response => response.data)
  }

}

export default CloudFunctionsApisUtils



// WEBPACK FOOTER //
// ./src/infra/CloudFunctionsApisUtils.js